// Gutter extension that renders a ▶ / ■ button next to every
// top-level `const` binding whose RHS produces audio (voice, stack,
// arrange, or ref). Clicking flips App's `soloId` — which the
// existing play pipeline picks up (see App.tsx:844), isolates via
// `isolateForSolo`, and plays on a loop until cleared. No new engine
// plumbing: we're just giving the code view a second trigger surface
// for the same solo mechanism the block editor uses.
//
// Reactive wiring: CodeMirror's state system is independent of React,
// so we bridge with a StateField + StateEffect. React dispatches
// `setSoloGutterState` whenever the parsed song, soloId, or click
// handler change; the gutter reads from the field. `lineMarkerChange`
// tells CodeMirror to re-query markers on those dispatches (otherwise
// the ▶/■ flip wouldn't show when soloId toggles).

import { gutter, GutterMarker } from '@codemirror/view';
import { StateEffect, StateField } from '@codemirror/state';
import type { Extension } from '@codemirror/state';

export type SoloGutterEntry = { from: number; blockId: string };

export type SoloGutterState = {
  entries: SoloGutterEntry[];
  soloId: string | null;
  onToggle: ((id: string) => void) | null;
};

const EMPTY_STATE: SoloGutterState = {
  entries: [],
  soloId: null,
  onToggle: null,
};

export const setSoloGutterState = StateEffect.define<SoloGutterState>();

const soloGutterField = StateField.define<SoloGutterState>({
  create: () => EMPTY_STATE,
  update(value, tr) {
    for (const effect of tr.effects) {
      if (effect.is(setSoloGutterState)) return effect.value;
    }
    return value;
  },
});

class SoloButtonMarker extends GutterMarker {
  constructor(
    readonly blockId: string,
    readonly isSoloed: boolean,
    readonly onToggle: ((id: string) => void) | null,
  ) {
    super();
  }
  override eq(other: SoloButtonMarker): boolean {
    return other.blockId === this.blockId && other.isSoloed === this.isSoloed;
  }
  override toDOM(): HTMLElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cm-solo-btn' + (this.isSoloed ? ' is-playing' : '');
    btn.textContent = this.isSoloed ? '■' : '▶';
    btn.setAttribute(
      'aria-label',
      this.isSoloed ? 'Stop solo' : 'Solo play (loops until stopped)',
    );
    btn.title = this.isSoloed
      ? 'Stop — clear solo'
      : 'Solo play — loops until you click again';
    // Prevent the gutter click from stealing focus or moving the caret.
    btn.addEventListener('mousedown', (e) => {
      e.preventDefault();
    });
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.onToggle?.(this.blockId);
    });
    return btn;
  }
}

// Spacer marker — rendered in a hidden measurement row by CodeMirror so
// the gutter reserves the button's width up front (otherwise the column
// flickers to its natural size the moment the first real button appears).
// Uses its own class + a plain span so the marker stays inert: the
// `.cm-solo-btn` selector hits real buttons only, and the spacer won't
// fight the real markers for event handling or accessibility tree slots.
class SoloSpacerMarker extends GutterMarker {
  override eq(other: SoloSpacerMarker): boolean {
    return other instanceof SoloSpacerMarker;
  }
  override toDOM(): HTMLElement {
    const el = document.createElement('span');
    el.className = 'cm-solo-btn-spacer';
    el.setAttribute('aria-hidden', 'true');
    el.textContent = '▶';
    return el;
  }
}

const SPACER = new SoloSpacerMarker();

const soloGutter = gutter({
  class: 'cm-solo-gutter',
  lineMarker(view, line) {
    const state = view.state.field(soloGutterField, false);
    if (!state || state.entries.length === 0) return null;
    const entry = state.entries.find(
      (e) => e.from >= line.from && e.from <= line.to,
    );
    if (!entry) return null;
    return new SoloButtonMarker(
      entry.blockId,
      state.soloId === entry.blockId,
      state.onToggle,
    );
  },
  lineMarkerChange(update) {
    return update.transactions.some((tr) =>
      tr.effects.some((e) => e.is(setSoloGutterState)),
    );
  },
  initialSpacer: () => SPACER,
});

export const playableSoloGutter: Extension = [soloGutterField, soloGutter];
